import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  FiMenu, FiX, FiUser, FiSearch, FiHome, 
  FiClock, FiStar, FiSettings, FiLogOut,
  FiBell, FiMapPin, FiCalendar, FiUsers, FiLoader
} from "react-icons/fi";
import { 
  FaCar, FaTaxi, FaMotorcycle, FaBus, 
  FaShieldAlt, FaWallet 
} from "react-icons/fa";
import { BsCurrencyRupee, BsPhone } from "react-icons/bs";
import { MdLocalOffer, MdSupportAgent } from "react-icons/md";
import { IoLocationSharp } from "react-icons/io5";
import logo from "../Assests/bookinindia-logo-optimized.png";

const NavBar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState("Fetching location...");
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [locationError, setLocationError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch user's live location
  useEffect(() => {
    const fetchLocation = () => {
      if (!navigator.geolocation) {
        setLocationError("Geolocation is not supported by your browser");
        setCurrentLocation("Location not available");
        setIsLoadingLocation(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            // Using OpenStreetMap Nominatim API for reverse geocoding
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
            );
            const data = await response.json();
            
            // Format the address to show a concise location
            const address = data.address;
            let locationString = "";
            
            if (address.road) {
              locationString += address.road;
              if (address.suburb) {
                locationString += `, ${address.suburb}`;
              }
            } else if (address.city_district || address.city) {
              locationString = address.city_district || address.city || "";
            } else if (address.town) {
              locationString = address.town;
            } else if (address.village) {
              locationString = address.village;
            } else {
              locationString = "Your Location";
            }
            
            setCurrentLocation(locationString);
            setIsLoadingLocation(false);
          } catch (error) {
            console.error("Error fetching location name:", error);
            setCurrentLocation("Location detected");
            setIsLoadingLocation(false);
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          switch (error.code) {
            case error.PERMISSION_DENIED:
              setLocationError("Location permission denied");
              setCurrentLocation("Location access denied");
              break;
            case error.POSITION_UNAVAILABLE:
              setLocationError("Location information unavailable");
              setCurrentLocation("Location unavailable");
              break;
            case error.TIMEOUT:
              setLocationError("Location request timeout");
              setCurrentLocation("Location timeout");
              break;
            default:
              setLocationError("Failed to get location");
              setCurrentLocation("Location error");
          }
          setIsLoadingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    };

    fetchLocation();
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isUserMenuOpen) setIsUserMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const closeAllMenus = () => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  // Cab booking specific navigation items
  const mainNavItems = [
    // { name: "Ride", icon: <FaCar size={18} />, path: "/ride" },
    // { name: "Rental", icon: <FaCar size={18} />, path: "/rental" },
    
    // { name: "Intercity", icon: <FaTaxi size={18} />, path: "/intercity" },
    // { name: "Outstation", icon: <IoLocationSharp size={18} />, path: "/outstation" },
    // { name: "Airport", icon: <MdSupportAgent size={18} />, path: "/airport" },
  ];

  const serviceItems = [
    { name: "Offers", icon: <MdLocalOffer size={18} />, path: "/offers" },
    { name: "Safety", icon: <FaShieldAlt size={18} />, path: "/safety" },
    { name: "Support", icon: <MdSupportAgent size={18} />, path: "/support" },
  ];

  const userMenuItems = [
    // { name: "My Profile", icon: <FiUser size={16} />, path: "/profile" },
    // { name: "My Rides", icon: <FaCar size={16} />, path: "/my-rides" },
    // { name: "Wallet", icon: <FaWallet size={16} />, path: "/wallet" },
    // { name: "Notifications", icon: <FiBell size={16} />, path: "/notifications" },
    // { name: "Settings", icon: <FiSettings size={16} />, path: "/settings" },
    { name: "Logout", icon: <FiLogOut size={16} />, path: "/logout" },
  ];

  const bottomNavItems = [
    { name: "Home", icon: <FiHome size={22} />, path: "/" },
    { name: "Ride", icon: <FaCar size={22} />, path: "/ride" },
    { name: "Rental", icon: <FaCar size={22} />, path: "/rental" },
    { name: "Offers", icon: <MdLocalOffer size={22} />, path: "/offers" },
    { name: "Profile", icon: <FiUser size={22} />, path: "/profile" },
  ];

  const handleSignInClick = () => {
    closeAllMenus();
    navigate("/sign-in");
  };

  // Function to retry location fetch
  const retryLocation = () => {
    setIsLoadingLocation(true);
    setLocationError("");
    setCurrentLocation("Fetching location...");
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
          );
          const data = await response.json();
          
          const address = data.address;
          let locationString = "";
          
          if (address.road) {
            locationString += address.road;
            if (address.suburb) {
              locationString += `, ${address.suburb}`;
            }
          } else if (address.city_district || address.city) {
            locationString = address.city_district || address.city || "";
          } else if (address.town) {
            locationString = address.town;
          } else if (address.village) {
            locationString = address.village;
          } else {
            locationString = "Your Location";
          }
          
          setCurrentLocation(locationString);
          setIsLoadingLocation(false);
        } catch (error) {
          setCurrentLocation("Location detected");
          setIsLoadingLocation(false);
        }
      },
      (error) => {
        setCurrentLocation("Location unavailable");
        setIsLoadingLocation(false);
      }
    );
  };

  return (
    <>
      <nav className="w-full bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="block" onClick={closeAllMenus}>
                <img 
                  src={logo} 
                  className="h-20 w-auto md:h-22 object-contain" 
                  alt="Cab Booking" 
                />
              </Link>
              
            </div>

            {/* Desktop Location Selector */}
            <div className="hidden lg:flex items-center space-x-4 flex-1 max-w-md mx-8">
              <div className="flex items-center space-x-2 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 w-full">
                <FiMapPin className="text-[#d60f0f] flex-shrink-0" size={20} />
                {isLoadingLocation ? (
                  <div className="flex items-center space-x-2 min-w-0 flex-1">
                    <FiLoader className="animate-spin text-gray-500 flex-shrink-0" size={16} />
                    <span className="text-sm text-gray-500 truncate">{currentLocation}</span>
                  </div>
                ) : locationError ? (
                  <div className="flex items-center space-x-2 w-full justify-between min-w-0">
                    <span className="text-sm text-gray-500 truncate">{currentLocation}</span>
                    <button 
                      onClick={retryLocation}
                      className="text-xs text-[#d60f0f] hover:underline flex-shrink-0"
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <input 
                    type="text" 
                    placeholder="Current Location"
                    className="bg-transparent outline-none text-sm w-full truncate"
                    value={currentLocation}
                    readOnly
                  />
                )}
              </div>
            </div>

            {/* Desktop Main Navigation - FIXED: Added flex-nowrap and overflow-visible */}
            <div className="hidden md:flex md:items-center md:space-x-1 flex-nowrap overflow-visible">
              {mainNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    isActive(item.path)
                    //   ? "text-indigo-700 bg-indigo-50"
                    //   : "text-gray-700 hover:text-indigo-700 hover:bg-indigo-50"
                  }`}
                >
                  {/* <span className={isActive(item.path) ? "text-indigo-700" : "text-indigo-600"}>
                    {item.icon}
                  </span> */}
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>

            {/* Desktop Right Section - FIXED: Added flex-nowrap */}
            <div className="hidden md:flex items-center space-x-2 flex-nowrap">
              {serviceItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="px-3 py-2 text-sm font-medium text-gray-700 rounded-lg transition-colors hover:text-[#d60f0f] hover:bg-red-50 whitespace-nowrap"
                >
                  {item.name}
                </Link>
              ))}
              
              {/* User Menu Dropdown */}
              <div className="relative flex-shrink-0">
                <button
                  onClick={handleSignInClick}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-[#d60f0f] text-white hover:bg-red-700 transition-colors whitespace-nowrap"
                >
                  <FiUser size={18} />
                  <span className="text-sm font-medium">Sign In</span>
                </button>

                {/* User Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    {userMenuItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors"
                        onClick={closeAllMenus}
                      >
                        <span className="text-red-600">{item.icon}</span>
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Right Section */}
            <div className="flex md:hidden items-center space-x-2 flex-nowrap">
              <Link
                to="/notifications"
                className="p-2 rounded-full text-gray-600 hover:bg-red-50 flex-shrink-0"
                onClick={closeAllMenus}
              >
                <FiBell size={20} />
              </Link>
              <button
                onClick={toggleMobileMenu}
                className="p-2 rounded-lg text-gray-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
              >
                {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Sidebar Menu */}
        {isMobileMenuOpen && (
          <>
            {/* Overlay */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
              onClick={closeAllMenus}
            />
            
            {/* Sidebar */}
            <div className="fixed top-0 left-0 h-full w-72 bg-white shadow-xl z-50 md:hidden overflow-y-auto animate-slideRight">
              {/* Sidebar Header */}
              <div className="bg-[#d60f0f] p-5 text-white">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                    <FiUser className="text-[#d60f0f]" size={24} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold truncate">Guest User</h3>
                    <p className="text-sm text-red-100 truncate">Sign in for more benefits</p>
                  </div>
                </div>
                <button 
                  onClick={handleSignInClick}
                  className="bg-white text-[#d60f0f] px-4 py-2 rounded-lg text-sm font-medium w-full hover:bg-red-50 transition-colors truncate"
                >
                  Sign In / Sign Up
                </button>
              </div>

              {/* Sidebar Content */}
              <div className="p-4">
                {/* Mobile Location with Live Update */}
                <div className="flex items-center space-x-3 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200 mb-4">
                  <FiMapPin className="text-[#d60f0f] flex-shrink-0" size={18} />
                  {isLoadingLocation ? (
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      <FiLoader className="animate-spin text-gray-500 flex-shrink-0" size={14} />
                      <span className="text-sm text-gray-500 truncate">{currentLocation}</span>
                    </div>
                  ) : locationError ? (
                    <div className="flex items-center justify-between flex-1 min-w-0">
                      <span className="text-sm text-gray-500 truncate">{currentLocation}</span>
                      <button 
                        onClick={retryLocation}
                        className="text-xs text-[#d60f0f] font-medium flex-shrink-0 ml-2"
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between flex-1 min-w-0">
                      <span className="text-sm text-gray-700 truncate">{currentLocation}</span>
                      <button 
                        onClick={retryLocation}
                        className="text-xs text-[#d60f0f] ml-2 flex-shrink-0"
                        title="Refresh location"
                      >
                        <FiMapPin size={14} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Main Navigation Section */}
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
                    Book a Ride
                  </h4>
                  {mainNavItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                        isActive(item.path)
                          ? "text-[#d60f0f] bg-red-50"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={closeAllMenus}
                    >
                      <span className={`flex-shrink-0 ${isActive(item.path) ? "text-[#d60f0f]" : "text-gray-500"}`}>
                        {item.icon}
                      </span>
                      <span className="truncate">{item.name}</span>
                    </Link>
                  ))}
                </div>

                <div className="border-t border-gray-100 my-3"></div>

                {/* Services Section */}
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
                    Services
                  </h4>
                  {serviceItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                        isActive(item.path)
                          ? "text-[#d60f0f] bg-red-50"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={closeAllMenus}
                    >
                      <span className={`flex-shrink-0 ${isActive(item.path) ? "text-[#d60f0f]" : "text-gray-500"}`}>
                        {item.icon}
                      </span>
                      <span className="truncate">{item.name}</span>
                    </Link>
                  ))}
                </div>

                <div className="border-t border-gray-100 my-3"></div>

                {/* Account Section */}
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
                    Account
                  </h4>
                  {userMenuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                        isActive(item.path)
                          ? "text-[#d60f0f] bg-red-50"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={closeAllMenus}
                    >
                      <span className={`flex-shrink-0 ${isActive(item.path) ? "text-[#d60f0f]" : "text-gray-500"}`}>
                        {item.icon}
                      </span>
                      <span className="truncate">{item.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </nav>

      {/* Spacer for bottom nav */}
      <div className="md:hidden h-16"></div>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideRight {
          from {
            opacity: 0;
            transform: translateX(-100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.2s ease-out;
        }
        .animate-slideRight {
          animation: slideRight 0.3s ease-out;
        }
        
        /* Additional utility classes for text overflow */
        .truncate {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        /* Prevent horizontal scroll on body */
        body {
          overflow-x: hidden;
        }
      `}</style>
    </>
  );
};

export default NavBar;
